import React, { useState, useMemo, useEffect } from 'react';
import { object, func } from 'prop-types';
import './StructureModeling.scss';
import apiClient from 'ps-components/lib/apiClient';
import Placeholder from 'ps-components/Placeholder/Placeholder';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import { showSlideout } from 'ps-components/Slideout/SlideoutReducer';
import LoadingIndicator from 'ps-components/LoadingIndicator/LoadingIndicator';
import { Button, Modal, DefaultModalDialog, Table, SearchBox, useDisclosure } from '../../ps-aspect-design';
import { useToast } from 'ps-shared-ui/v4/Toast';
import { SLIDEOUTS } from '../Slideout/Slideouts';
import { setBulkCopyData } from './StructureBulkCopy/StructureBulkCopyActions';
import { setStrucBulkCreateGridStore } from './StructureBulkCreate/StructureBulkCreateActions';
import useStickyState from './useStickyState';
import { SMA_BASIS_KEY } from './StructureModelAnalytics/StructureModelingAnalyticsSlideout';

import {
  formatDifferentialHeader,
  formatDifferentialCell,
  formatModelNameCell,
  formatDateCell
} from './StructureModelingCellRenderers';

const scopes = [
  { name: 'personal', displayName: 'Personal Models' },
  { name: 'site', displayName: 'Site Models' }
];

const costBasisOptionsDefault = [
  {
    value: 'base_salary',
    label: 'Base Salary'
  },
  {
    value: 'total_cash',
    label: 'Total Cash'
  },
  {
    value: 'total_target_cash',
    label: 'Total Target Cash'
  },
  {
    value: 'total_direct',
    label: 'Total Direct'
  },
  {
    value: 'total_guaranteed_pay',
    label: 'Total Guaranteed Pay'
  }
];

function filterScopeOptions(scopes, rights, onlyHasAccessToSiteLevelGroup) {
  const additionalScopes = [];
  if (!onlyHasAccessToSiteLevelGroup) {
    additionalScopes.push({ name: 'group', displayName: 'Group Models' });
  }
  if (rights?.siteAdmin || rights?.superAdmin) {
    additionalScopes.push({ name: 'all_personal', displayName: 'All Personal Models' });
  }
  return [...scopes].concat(additionalScopes);
}

const StructureModeling = props => {
  const {
    history,
    dispatch,
    session: {
      user: {
        preferences: { numericRounding, percentRounding }
      }
    }
  } = props;
  const STRUCTURES_URL = `/iframe/merit-${window.app.subsite}/structures/struc/structures`;
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [data, setData] = useState([]);
  const [modelIdsForDelete, setModelIdsForDelete] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopeValue, setScopeValue] = useStickyState('Site Models', 'structure-modeling-scope');
  const [tableState, setTableState] = useState({
    scopeOptions: filterScopeOptions(scopes, props.session?.user?.rights, props.session?.onlyHasAccessToSiteLevelGroup),
    pagination: { pageIndex: 0, pageSize: 50 },
    sort: [],
    searchValue: '',
    scopeValue
  });
  const [tableInstance, setTableInstance] = useState();
  const [costBasisOptions, setCostBasisOptions] = useState(costBasisOptionsDefault);
  const [selectedCostBase, setSelectedCostBase] = useState('');
  const baseFromLocalStorage = localStorage.getItem(SMA_BASIS_KEY + '_' + props.session.organization.organizationId);

  // If the user does not have access to see a scope option,
  // reset that user to "Site Structures"
  useEffect(() => {
    if (!tableState.scopeOptions?.find(scope => scope.displayName === scopeValue)) setScopeValue('Site Models');
  }, []);

  useEffect(() => {
    setTableState({
      ...tableState,
      scopeOptions: filterScopeOptions(
        scopes,
        props.session?.user?.rights,
        props.session?.onlyHasAccessToSiteLevelGroup
      )
    });
  }, [props.session?.user]);

  // TODO: Init counts at zero once fix for: https://bitbucket.org/payscale/aspect-design-system/src/898b04d6e2ef195d46d03a6c2299fc205babdb39/src/components/Table/Table.tsx#lines-190
  const [totalRowCount, setTotalRowCount] = useState(1);
  const [totalPageCount, setTotalPageCount] = useState(1);

  const defaultColumn = useMemo(
    () => ({
      width: 145
    }),
    []
  );

  const handlePagination = (pageIndex, pageSize) => {
    if (pageIndex !== tableState.pagination.pageIndex || pageSize !== tableState.pagination.pageSize)
      setTableState({ ...tableState, pagination: { pageIndex, pageSize } });
  };

  const getRowId = React.useCallback((row, relativeIndex) => `${row['model_id']}_${relativeIndex}`, []);

  const onCostBasisChange = option => {
    const basisValue = costBasisOptions.find(({ label }) => label === option).value;
    localStorage.setItem(SMA_BASIS_KEY + '_' + props.session.organization.organizationId, basisValue);
    setSelectedCostBase(basisValue);
    fetchData(tableState.pagination.pageIndex, tableState.pagination.pageSize, basisValue);
  };

  const fetchCostBasis = React.useCallback(async () => {
    const response = await apiClient.apiPost('/api/structure-modeling/analytics/grade-list/getUDFs');
    let costBasisOptionsWithUdf = costBasisOptions;
    if (response?.data?.udfFields?.rows) {
      const udfFormat = response.data.udfFields.rows.map(udf => {
        return {
          label: udf.udfName,
          value: udf.udfField
        };
      });

      costBasisOptionsWithUdf = costBasisOptions.concat(udfFormat);
    }
    return costBasisOptionsWithUdf;
  });

  const fetchData = React.useCallback(
    async (pageIndex, pageSize, costBase) => {
      if (!costBase) {
        if (!selectedCostBase) {
          const costBasisOptionsWithUdf = await fetchCostBasis();
          setCostBasisOptions(costBasisOptionsWithUdf);
          costBase = costBasisOptionsWithUdf.some(e => e.value === baseFromLocalStorage)
            ? baseFromLocalStorage
            : 'base_salary';
          setSelectedCostBase(costBase);
        } else {
          costBase = selectedCostBase;
        }
      }

      try {
        setLoading(true);
        const usedScopeOptions = tableState.scopeOptions ?? scopes;
        const scope = usedScopeOptions.find(scope => scope.displayName === tableState.scopeValue);
        const options = {
          filter: {
            where: {
              and: [{ filter: { eq: scope.name, term: 'scope' } }]
            }
          },
          limit: tableState.pagination.pageSize,
          offset: tableState.pagination.pageIndex * tableState.pagination.pageSize,
          orderBy: tableState.sort,
          costBase
        };
        if (tableState.searchValue) {
          options.filter['fullTextSearch'] = tableState.searchValue;
        }
        const response = await apiClient.apiPost('/api/structures/generic-grid/getModelList', options);
        const { terms, data, metadata } = response.data.result;
        const mappedData = data.map(orderedRow => {
          const dataByTerm = orderedRow.reduce((acc, value, index) => {
            acc[terms[index].term] = value;
            return acc;
          }, {});
          dataByTerm['model_name'] = {
            modelName: dataByTerm['model_name'],
            modelId: dataByTerm['model_id']
          };
          dataByTerm['p_below_min'] = {
            type: dataByTerm['type'],
            current: parseInt(dataByTerm['c_below_min']),
            proposed: parseInt(dataByTerm['p_below_min']),
            digits: 0
          };
          dataByTerm['p_above_max'] = {
            type: dataByTerm['type'],
            current: parseInt(dataByTerm['c_above_max']),
            proposed: parseInt(dataByTerm['p_above_max']),
            digits: 0
          };
          dataByTerm['p_cost_to_move_min'] = {
            type: dataByTerm['type'],
            current: parseFloat(dataByTerm['c_cost_to_move_min']),
            proposed: parseFloat(dataByTerm['p_cost_to_move_min']),
            digits: numericRounding
          };
          dataByTerm['p_cost_above_max'] = {
            type: dataByTerm['type'],
            current: parseFloat(dataByTerm['c_cost_above_max']),
            proposed: parseFloat(dataByTerm['p_cost_above_max']),
            digits: numericRounding
          };
          dataByTerm['p_avg_compa_ratio'] = {
            type: dataByTerm['type'],
            current: parseFloat(dataByTerm['c_avg_compa_ratio'] * 100),
            proposed: parseFloat(dataByTerm['p_avg_compa_ratio'] * 100),
            digits: percentRounding
          };
          dataByTerm['p_avg_range_pen'] = {
            type: dataByTerm['type'],
            current: parseFloat(dataByTerm['c_avg_range_pen'] * 100),
            proposed: parseFloat(dataByTerm['p_avg_range_pen'] * 100),
            digits: percentRounding
          };
          return dataByTerm;
        });
        setTotalRowCount(metadata.totalCount);
        setTotalPageCount(Math.ceil(metadata.totalCount / metadata.limit));

        setData(mappedData);
      } catch (error) {
        toast('There was a problem loading the models.', {
          className: 'structure-modeling__error-toast',
          duration: 3000,
          isCloseable: true,
          type: 'error'
        });
      } finally {
        setLoading(false);
      }
    },
    [tableState]
  );

  return (
    <div className="structure-modeling aspect-base">
      <LoadingIndicator loading={loading} />
      <div className="structure-modeling__title-scope">
        <h1 className="structure-modeling__title">Structure Modeling</h1>
        <div className="structure-modeling__dropdown-wrapper">
          <label htmlFor="structure-modeling__dropdown">Calculate the cost based on</label>
          <Dropdown
            id="structure-modeling__dropdown"
            className="structure-modeling__dropdown"
            dropdownItems={costBasisOptions.map(option => option.label)}
            itemOnClick={e => onCostBasisChange(e)}
            text={selectedCostBase ? costBasisOptions.find(({ value }) => value === selectedCostBase).label : ''}
            rightBound={false}
            dropdownClass="structure-modeling__cost-base-dropdown"
          />
          <label htmlFor="structure-modeling__dropdown">Scope</label>
          <Dropdown
            id="structure-modeling__dropdown"
            className="structure-modeling__dropdown"
            text={tableState.scopeValue}
            itemOnClick={value => {
              setTableState({
                ...tableState,
                pagination: { pageIndex: 0, pageSize: tableState.pagination.pageSize },
                scopeValue: value
              });
              setScopeValue(value);
            }}
            dropdownItems={
              tableState.scopeOptions
                ? tableState.scopeOptions.map(scope => scope.displayName)
                : scopes.map(scope => scope.displayName)
            }
          />
        </div>
      </div>
      <div className="structure-modeling__search-create">
        <SearchBox
          aria-label="search"
          id="structure-modeling__search"
          inputClassName="structure-modeling__search"
          placeholder="Model name, type, structure code, currency, or user name"
          onClear={() =>
            setTableState({
              ...tableState,
              pagination: { pageIndex: 0, pageSize: tableState.pagination.pageSize },
              searchValue: ''
            })
          }
          onSearch={value =>
            setTableState({
              ...tableState,
              pagination: { pageIndex: 0, pageSize: tableState.pagination.pageSize },
              searchValue: value
            })
          }
        />
        <div className="structure-modeling__create">
          <Button
            className="structure-modeling__create-based-btn"
            data-testid="test-id-here"
            id="button-id-here"
            size="default"
            variant="link"
            onClick={() => {
              tableInstance?.toggleAllRowsSelected(false);
              const gridStore = {
                load: () => {
                  tableInstance?.toggleAllRowsSelected(false);
                  fetchData(tableState.pagination.pageIndex, tableState.pagination.pageSize);
                }
              };
              dispatch(setStrucBulkCreateGridStore(gridStore));
              dispatch(showSlideout(SLIDEOUTS.STRUCTURE_BULK_CREATE));
            }}
          >
            Create Models Based on Existing Structures
          </Button>
          <Button
            variant="primary"
            size="default"
            className="structure-modeling__create-btn"
            onClick={() =>
              history.push({
                pathname: STRUCTURES_URL,
                state: { fromStructureModeling: true }
              })
            }
          >
            Create New Structure
          </Button>
        </div>
      </div>
      <div className="structure-modeling__grid-wrapper">
        <Table
          compact={true}
          sortEnabled={true}
          getRowId={getRowId}
          emptyComponent={
            <Placeholder
              className={'structure-modeling__placeholder-container'}
              primaryText={
                `No model(s) found in ${scopeValue} ` +
                (tableState.searchValue ? `with search value “ ${tableState.searchValue} ”` : '')
              }
              secondaryText={
                `Please select a different scope ` + (tableState.searchValue ? `or try a different search value` : '')
              }
              showText={true}
            />
          }
          onSort={(term, dir) => {
            setTableState({ ...tableState, sort: [{ term, sort: dir === 'ascending' ? 'asc' : 'desc' }] });
          }}
          className="structure-modeling__grid"
          columns={[
            {
              ...defaultColumn,
              id: 'model_name',
              name: 'Model Name',
              formatCell: formatModelNameCell,
              width: 215
            },
            {
              ...defaultColumn,
              id: 'type',
              name: 'Type',
              columnClassName: 'structure-modeling__type-cell'
            },
            {
              ...defaultColumn,
              id: 'structure_code',
              name: 'Structure Code',
              columnClassName: 'structure-modeling__structure-code-cell'
            },
            {
              ...defaultColumn,
              id: 'currency',
              name: 'Currency',
              width: 95
            },
            {
              ...defaultColumn,
              id: 'p_job_count',
              contentAlign: 'right',
              name: 'Jobs',
              width: 100
            },
            {
              ...defaultColumn,
              id: 'p_ee_count',
              contentAlign: 'right',
              name: 'Employees'
            },
            {
              ...defaultColumn,
              id: 'p_below_min',
              name: 'EEs Below Min',
              contentAlign: 'right',
              formatHeader: formatDifferentialHeader,
              formatCell: formatDifferentialCell
            },
            {
              ...defaultColumn,
              id: 'p_above_max',
              name: 'EEs Above Max',
              contentAlign: 'right',
              formatHeader: formatDifferentialHeader,
              formatCell: formatDifferentialCell
            },
            {
              ...defaultColumn,
              id: 'p_cost_to_move_min',
              name: 'Cost to Move to Min',
              width: 165,
              contentAlign: 'right',
              formatHeader: formatDifferentialHeader,
              formatCell: formatDifferentialCell
            },
            {
              ...defaultColumn,
              id: 'p_cost_above_max',
              name: 'Amt Above Max',
              contentAlign: 'right',
              formatHeader: formatDifferentialHeader,
              formatCell: formatDifferentialCell
            },
            {
              ...defaultColumn,
              id: 'p_avg_compa_ratio',
              name: 'Avg Compa Ratio',
              contentAlign: 'right',
              formatHeader: formatDifferentialHeader,
              formatCell: formatDifferentialCell
            },
            {
              ...defaultColumn,
              id: 'p_avg_range_pen',
              name: 'Avg Range Penetration',
              width: 185,
              contentAlign: 'right',
              formatHeader: formatDifferentialHeader,
              formatCell: formatDifferentialCell
            },
            { ...defaultColumn, id: 'creation_user', name: 'Created By' },
            {
              ...defaultColumn,
              id: 'creation_time',
              name: 'Creation Date',
              formatCell: formatDateCell,
              width: 160
            },
            {
              ...defaultColumn,
              id: 'last_modifying_user',
              name: 'Last Modifying User',
              width: 180
            },
            {
              ...defaultColumn,
              id: 'last_modified',
              name: 'Last Modified Date',
              formatCell: formatDateCell,
              width: 160
            }
          ]}
          data={data}
          enablePagination
          fixedHeader
          paginationProps={{
            dropUp: true,
            pageSize: tableState.pagination.pageSize,
            pageIndex: tableState.pagination.pageIndex,
            pageSizeOptions: [25, 50, 100],
            totalPageCount: totalPageCount,
            totalRowCount: totalRowCount,
            manualPagination: {
              onPaginate: handlePagination,
              updatePageData: fetchData
            },
            pageIsLoading: loading
          }}
          rowSelectionEnabled
          toolbar={{
            actions: [
              {
                actionIconName: 'Docs',
                actionName: 'Copy Model',
                isActionEnabled: () => true,
                onAction: (_currentPageSelectedRows, allSelectedRowIds) => {
                  // rowId is [model_id]_[relativeIndex], so split at the underscore to get the model_id
                  const modelIds = allSelectedRowIds.map(id => parseInt(id.split('_', 1)[0]));
                  const uniqueModelIds = [...new Set(modelIds)];
                  const gridStore = {
                    load: () => {
                      tableInstance?.toggleAllRowsSelected(false);
                      fetchData(tableState.pagination.pageIndex, tableState.pagination.pageSize);
                    }
                  };
                  dispatch(setBulkCopyData(uniqueModelIds, gridStore));
                  dispatch(showSlideout(SLIDEOUTS.STRUCTURE_BULK_COPY));
                }
              },
              {
                actionIconName: 'Trash',
                actionName: 'Delete Model',
                isActionEnabled: () => true,
                onAction: (_currentPageSelectedRows, allSelectedRowIds) => {
                  // rowId is [model_id]_[relativeIndex], so split at the underscore to get the model_id
                  const modelIds = allSelectedRowIds.map(id => parseInt(id.split('_', 1)[0]));
                  const uniqueModelIds = [...new Set(modelIds)];
                  setModelIdsForDelete(uniqueModelIds);
                  onOpen();
                }
              }
            ]
          }}
          onTableInstanceChange={setTableInstance}
        />
      </div>
      <Modal isOpen={isOpen} onClose={onClose} className="structure-modeling__delete-dialog">
        <DefaultModalDialog
          primaryText="Delete"
          secondaryText="Cancel"
          primaryCallback={async () => {
            try {
              if (modelIdsForDelete.length) {
                const results = await apiClient.apiPost('api/structures/model/deleteModel', {
                  model_id_list: modelIdsForDelete
                });
                const successList = results.data.structuresList.filter(struc => struc.success === true);
                const failedList = results.data.structuresList.filter(struc => struc.success === false);
                successList.forEach(struc => {
                  toast(`Model ${struc.modelName} deleted successfully`, {
                    duration: 3000,
                    isCloseable: false,
                    type: 'success'
                  });
                });

                failedList.forEach(struc => {
                  const reportsList = struc.reports.map(name => '<li>' + name + '</li>').join('');
                  const matricesList = struc.matrices.map(name => '<li>' + name + '</li>').join('');
                  const errorMessage = 
                    reportsList && matricesList ?
                    `Model ${struc.modelName} failed to delete because there are report(s) and matrix model(s) affiliated with it. <br>Reports: ${reportsList} Matrix models: ${matricesList}` :
                    reportsList ?
                    `Model ${struc.modelName} failed to delete because there are report(s) affiliated with it. Reports: ${reportsList}` :
                    matricesList ?
                    `Model ${struc.modelName} failed to delete because there are matrix model(s) affiliated with it. Matrix models: ${matricesList}` :
                    `Model ${struc.modelName} failed to delete`
                  ;

                  toast(errorMessage, {
                    duration: 10000,
                    isCloseable: true,
                    type: 'error'
                  });
                });
              }
              tableInstance?.toggleAllRowsSelected(false);
              const mutatedData = [...data];
              modelIdsForDelete.forEach(modelId => {
                const index = mutatedData.findIndex(row => row['model_name'].modelId === modelId);
                if (index !== -1) mutatedData.splice(index, 1);
              });
              setData(mutatedData);
              setModelIdsForDelete([]);
              fetchData(tableState.pagination.pageIndex, tableState.pagination.pageSize);
            } catch (error) {
              toast('There was a problem deleting the model.', {
                duration: 3000,
                isCloseable: false,
                type: 'error'
              });
            } finally {
              onClose();
            }
          }}
          secondaryCallback={onClose}
          title="Confirm Delete"
          primaryDisabled={false}
          secondaryDisabled={false}
        >
          <p>{`You have selected to delete ${modelIdsForDelete.length} model${
            modelIdsForDelete.length !== 1 ? 's' : ''
          }. Please confirm your choice, or cancel and return to the page.`}</p>
        </DefaultModalDialog>
      </Modal>
    </div>
  );
};

StructureModeling.propTypes = {
  history: object,
  dispatch: func,
  session: object
};

export default StructureModeling;
